import { createReducer, on } from '@ngrx/store';
import * as CaNumbersActions from './ca-numbers.actions';
import { CaNumber, SingleCaNumberDetails } from './ca-numbers.model';
import { clearSelectedCaNumber, deleteCaNumberFailure } from './ca-numbers.actions';

export const CA_NUMBERS_LAST_ACTIONS = {
  DELETE_CA_NUMBER_SUCCESS: 'Delete CaNumber Success',
  DELETE_CA_NUMBER_FAILURE: 'Delete CaNumber Failure'
}

export interface CaNumbersState {
  caNumbers: CaNumber[];
  total: number;
  loading: boolean;
  submitting: boolean;
  error: any;
  offset: number;
  limit: number;
  sort: string;
  q: string;
  selectedCaNumber: SingleCaNumberDetails | null;
  previousOffset: number | null;
}

export const initialState: CaNumbersState = {
  caNumbers: [],
  total: 0,
  loading: false,
  submitting: false,
  error: null,
  offset: 0,
  limit: 10,
  sort: 'id',
  q: '',
  selectedCaNumber: null,
  previousOffset: null,
};

export const caNumbersReducer = createReducer(
  initialState,
  on(CaNumbersActions.loadCaNumbers, (state, { q, offset, limit, sort }) => ({
    ...state,
    loading: true,
    error: null,
    q: q ?? state.q,
    offset: offset ?? state.offset,
    limit: limit ?? state.limit,
    sort: sort ?? state.sort,
    // If we explicitly load with an empty query, treat it as a full reset of search
    previousOffset: q === '' ? null : state.previousOffset,
  })),
  on(CaNumbersActions.loadCaNumbersSuccess, (state, { caNumbers, total }) => ({
    ...state,
    caNumbers,
    total,
    loading: false,
    error: null,
  })),
  on(CaNumbersActions.loadCaNumbersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(CaNumbersActions.setPage, (state, { offset, limit }) => ({
    ...state,
    offset,
    limit,
  })),
  on(CaNumbersActions.setSort, (state, { sort }) => ({
    ...state,
    sort,
  })),
  on(CaNumbersActions.setSearch, (state, { q }) => {
    const isSearching: boolean = !!q && q.length > 0;

    if (isSearching) {
      // When starting a search from a non-search state, remember the current offset
      const rememberedOffset = state.q ? state.previousOffset : state.offset;
      return {
        ...state,
        q,
        offset: 0,
        previousOffset: rememberedOffset ?? state.previousOffset,
      };
    }

    // Search cleared: restore previous offset if we have one
    return {
      ...state,
      q: '',
      offset: state.previousOffset ?? state.offset,
      previousOffset: null,
    };
  }),
  on(CaNumbersActions.deleteCaNumberSuccess, (state, { id }) => ({
    ...state,
    caNumbers: state.caNumbers.filter(caNumber => caNumber.id !== id),
    total: state.total - 1,
    lastAction: CA_NUMBERS_LAST_ACTIONS.DELETE_CA_NUMBER_SUCCESS,
  })),
  on(CaNumbersActions.deleteCaNumberFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    lastAction: CA_NUMBERS_LAST_ACTIONS.DELETE_CA_NUMBER_FAILURE,
  })),
  on(CaNumbersActions.loadSingleCaNumber, (state) => ({
    ...state,
    loading: true,
    error: null,
    selectedCaNumber: null,
  })),
  on(CaNumbersActions.loadSingleCaNumberSuccess, (state, { caNumber }) => ({
    ...state,
    loading: false,
    selectedCaNumber: caNumber,
    error: null,
  })),
  on(CaNumbersActions.loadSingleCaNumberFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    selectedCaNumber: null,
  })),
  on(CaNumbersActions.updateCaNumber, (state) => ({
    ...state,
    submitting: true,
    error: null,
  })),
  on(CaNumbersActions.updateCaNumberSuccess, (state, { caNumber }) => ({
    ...state,
    submitting: false,
    selectedCaNumber: caNumber,
    error: null,
    caNumbers: state.caNumbers.map((item) =>
      item.id === caNumber.id ? { ...item, ...caNumber } : item
    ),
  })),
  on(CaNumbersActions.updateCaNumberFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),
  on(CaNumbersActions.addCaNumber, (state) => ({
    ...state,
    submitting: true,
    error: null,
  })),
  on(CaNumbersActions.addCaNumberSuccess, (state, { caNumber }) => ({
    ...state,
    submitting: false,
    error: null,
    caNumbers: [...state.caNumbers, caNumber],
    total: state.total + 1,
  })),
  on(CaNumbersActions.addCaNumberFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),
  on(CaNumbersActions.addCaFromOrder, (state) => ({
    ...state,
    submitting: true,
    error: null,
  })),
  on(CaNumbersActions.addCaFromOrderSuccess, (state, { caNumber }) => ({
    ...state,
    submitting: false,
    error: null,
    caNumbers: [...state.caNumbers, caNumber],
    total: state.total + 1,
    selectedCaNumber: caNumber,
  })),
  on(CaNumbersActions.addCaFromOrderFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),
  on(clearSelectedCaNumber, (state) => ({
    ...state,
    selectedCaNumber: null,
  }))
);
